import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useMall } from "@/context/MallContext";
import { sellersOf } from "@/lib/sellers";
import StorePicker from "@/components/mall/StorePicker";

const TONES = {
    green: { background: "#2563EB", color: "#ffffff" },
    ink: { background: "#0B0B0B", color: "#ffffff" },
    blue: { background: "#2563eb", color: "#ffffff" },
    paper: { background: "#ffffff", color: "#0B0B0B", border: "1px solid #d4d4d8" },
    outline: { background: "transparent", color: "#ffffff", border: "1px solid rgba(255,255,255,0.28)" },
    quiet: { background: "transparent", color: "#3f3f46", border: "1px solid #d4d4d8" },
};

/**
 * A mall billboard is generic, so its buttons ask which shop first. One seller in the
 * mall for that room and there's nothing to ask — we walk you straight in.
 */
export default function ShopButton({ room, path, title, children, testid, tone = "ink", size = "lg", className = "" }) {
    const { enter, solo, site, store } = useMall();
    const navigate = useNavigate();
    const [stores, setStores] = useState([]);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    const href = (slug) => (typeof path === "function" ? path(slug) : path);

    const walkIn = (card) => {
        setOpen(false);
        if (card) enter(card, "picker");
        else toast.message("Couldn't reach the directory — opening the house shop.");
        navigate(href(card?.slug || ""));
    };

    const click = async () => {
        // Standing in a shop already (its page, its own site or the demo)? There is nobody else to choose from.
        if (solo || store) {
            setOpen(false);
            if (solo && site?.store) enter(site.store);
            navigate(href(solo || store.slug));
            return;
        }
        setBusy(true);
        const rows = await sellersOf(room);
        setBusy(false);
        setStores(rows);
        if (rows.length <= 1) return walkIn(rows[0]);
        setOpen(true);
    };

    return (
        <>
            <button onClick={click} disabled={busy} data-testid={testid}
                className={`inline-flex items-center justify-center gap-2 rounded-full font-bold uppercase tracking-wide transition-transform duration-200 hover:scale-[1.03] active:scale-95 disabled:opacity-60 ${size === "lg" ? "px-7 py-3.5 text-sm" : "px-5 py-2.5 text-xs"} ${className}`}
                style={TONES[tone] || TONES.ink}>
                {busy ? <Loader2 size={15} className="animate-spin" /> : null}
                {children}
            </button>
            <StorePicker open={open} title={title} room={room} stores={stores}
                onPick={walkIn} onClose={() => setOpen(false)} />
        </>
    );
}
